import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box, Card, Typography, Button, TextField, IconButton,
  Alert, CircularProgress, Skeleton, Select, MenuItem,
  FormControl, Dialog, DialogTitle, DialogContent,
  DialogActions, Chip, Tooltip
} from '@mui/material';
import {
  Add, Search, Close, Edit, Delete, ArrowBack,
  WbSunny, Nightlight, CloudOff, CloudDone, Sync
} from '@mui/icons-material';
import Layout from '../components/Layout';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { canUser } from '../utils/permissions';
import api from '../api/authApi';

const todayStr = () => new Date().toISOString().split('T')[0];
const fmt2 = v => parseFloat(v||0).toLocaleString('en-IN',{minimumFractionDigits:2,maximumFractionDigits:2});

// ═══════════════════════════════════════════════════════════
//  OFFLINE QUEUE — IndexedDB
// ═══════════════════════════════════════════════════════════
const DB_NAME      = 'DairyProOffline';
const DB_VERSION   = 2;
const SALE_STORE   = 'milksale_queue';

function openMilkSaleDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains('collections_queue'))
        db.createObjectStore('collections_queue', { keyPath: 'localId', autoIncrement: true });
      if (!db.objectStoreNames.contains(SALE_STORE))
        db.createObjectStore(SALE_STORE, { keyPath: 'localId', autoIncrement: true });
    };
    req.onsuccess = (e) => resolve(e.target.result);
    req.onerror   = (e) => reject(e.target.error);
  });
}

async function queueSaleOffline(payload) {
  const db = await openMilkSaleDB();
  return new Promise((resolve, reject) => {
    const req = db.transaction(SALE_STORE, 'readwrite')
      .objectStore(SALE_STORE)
      .add({ ...payload, status: 'pending', createdAt: new Date().toISOString() });
    req.onsuccess = () => resolve(req.result);
    req.onerror   = () => reject(req.error);
  });
}

async function getAllSaleQueue() {
  const db = await openMilkSaleDB();
  return new Promise((resolve, reject) => {
    const req = db.transaction(SALE_STORE, 'readonly').objectStore(SALE_STORE).getAll();
    req.onsuccess = () => resolve(req.result);
    req.onerror   = () => reject(req.error);
  });
}

async function updateSaleEntry(localId, updates) {
  const db = await openMilkSaleDB();
  return new Promise((resolve, reject) => {
    const store = db.transaction(SALE_STORE, 'readwrite').objectStore(SALE_STORE);
    const g = store.get(localId);
    g.onsuccess = () => {
      const p = store.put({ ...g.result, ...updates });
      p.onsuccess = () => resolve();
      p.onerror   = () => reject(p.error);
    };
  });
}

async function clearSyncedSales() {
  const db = await openMilkSaleDB();
  return new Promise((resolve) => {
    const store = db.transaction(SALE_STORE, 'readwrite').objectStore(SALE_STORE);
    const req = store.getAll();
    req.onsuccess = () => {
      req.result.filter(r => r.status === 'synced').forEach(r => store.delete(r.localId));
      resolve();
    };
  });
}

async function syncSaleQueue() {
  const all = await getAllSaleQueue();
  const pending = all.filter(e => e.status === 'pending');
  let synced = 0, failed = 0;
  for (const entry of pending) {
    try {
      const { localId, status, createdAt, syncedAt, error: _e, ...payload } = entry;
      const res = await api.post('/milk-sale', payload);
      if (res.data?.success) {
        await updateSaleEntry(localId, { status: 'synced', syncedAt: new Date().toISOString() });
        synced++;
      } else {
        await updateSaleEntry(localId, { status: 'failed', error: res.data?.message });
        failed++;
      }
    } catch (err) {
      await updateSaleEntry(entry.localId, { status: 'failed', error: err.message });
      failed++;
    }
  }
  await clearSyncedSales();
  return { synced, failed };
}

// ── useOnline hook — real connectivity check ────────────────
function useOnline() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  const checkReal = useCallback(async () => {
    if (!navigator.onLine) { setIsOnline(false); return; }
    try {
      await api.get('/health', { timeout: 3000 });
      setIsOnline(true);
    } catch {
      setIsOnline(false);
    }
  }, []);

  useEffect(() => {
    checkReal();
    const on  = () => checkReal();
    const off = () => setIsOnline(false);
    window.addEventListener('online',  on);
    window.addEventListener('offline', off);
    const timer = setInterval(checkReal, 15000);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
      clearInterval(timer);
    };
  }, [checkReal]);

  return isOnline;
}

// ── OfflineBanner ───────────────────────────────────────────
function OfflineBanner({ isOnline, pendingCount, syncing, onSync, themeColor }) {
  if (isOnline && pendingCount === 0) return null;
  return (
    <Box sx={{
      mb: 2, p: 1.5, borderRadius: 2,
      background: isOnline ? '#e8f5e9' : '#fff3e0',
      border: `1px solid ${isOnline ? '#a5d6a7' : '#ffcc80'}`,
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {isOnline
          ? <CloudDone sx={{ color: '#388e3c', fontSize: 20 }} />
          : <CloudOff   sx={{ color: '#f57c00', fontSize: 20 }} />}
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: 13, color: isOnline ? '#2e7d32' : '#e65100' }}>
            {isOnline ? 'Back Online' : 'Offline Mode'}
          </Typography>
          <Typography sx={{ fontSize: 12, color: '#666' }}>
            {pendingCount} milk sale{pendingCount !== 1 ? 's' : ''} pending
            {!isOnline && ' — will sync when connected'}
          </Typography>
        </Box>
      </Box>
      {isOnline && pendingCount > 0 && (
        <Button size="small" variant="contained" onClick={onSync} disabled={syncing}
          startIcon={syncing ? <CircularProgress size={14} color="inherit" /> : <Sync />}
          sx={{ background: themeColor, borderRadius: 2, fontWeight: 700, fontSize: 12, textTransform: 'none' }}>
          {syncing ? 'Syncing...' : `Sync Now (${pendingCount})`}
        </Button>
      )}
    </Box>
  );
}

// ═══════════════════════════════════════════════════════════
//  MAIN: Milk Sale (router between views)
// ═══════════════════════════════════════════════════════════
export default function MilkSale() {
  const location = useLocation();
  const initView = location.pathname === '/customers' ? 'customers' : 'sale';
  const [view, setView] = useState(initView);

  if (view === 'customers') return <MilkSaleCustomers onBack={() => { setView('sale'); window.history.pushState({},'','/milk-sale'); }} />;
  if (view === 'add')       return <AddMilkSaleView   onBack={() => setView('sale')} onSaved={() => setView('sale')} />;
  return <MilkSaleList onAdd={() => setView('add')} onCustomers={() => setView('customers')} />;
}

// ═══════════════════════════════════════════════════════════
//  VIEW 1: Milk Sale List
// ═══════════════════════════════════════════════════════════
function MilkSaleList({ onAdd, onCustomers = () => {} }) {
  const { user } = useAuth();
  const canAdd = canUser(user, 'MilkSale', 'add');
  const canUpdate = canUser(user, 'MilkSale', 'update');
  const canDelete = canUser(user, 'MilkSale', 'delete');
  const { theme } = useApp();
  const isOnline = useOnline();

  const [date,         setDate]         = useState(todayStr());
  const [shift,        setShift]        = useState('');
  const [milkType,     setMilkType]     = useState('');
  const [search,       setSearch]       = useState('');
  const [rows,         setRows]         = useState([]);
  const [total,        setTotal]        = useState(0);
  const [loading,      setLoading]      = useState(false);
  const [page,         setPage]         = useState(1);
  const [pageSize,     setPageSize]     = useState(20);
  const [error,        setError]        = useState('');
  const [success,      setSuccess]      = useState('');
  const [pendingCount, setPendingCount] = useState(0);
  const [syncing,      setSyncing]      = useState(false);

  // Load pending count from IndexedDB
  const refreshPending = useCallback(async () => {
    try {
      const all = await getAllSaleQueue();
      setPendingCount(all.filter(e => e.status === 'pending').length);
    } catch { }
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/milk-sale', {
        params: { saleDate: date, shift, milkType, search, page, pageSize }
      });
      if (res.data?.success) {
        setRows(res.data.data?.data || []);
        setTotal(res.data.data?.totalCount ?? 0);
      } else setError(res.data?.message || 'Load failed.');
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [date, shift, milkType, search, page, pageSize]);

  useEffect(() => { fetchData(); refreshPending(); }, [fetchData, refreshPending]);

  // Auto-sync when coming online
  useEffect(() => {
    if (isOnline && pendingCount > 0) handleSync();
  }, [isOnline]); // eslint-disable-line

  const handleSync = async () => {
    if (syncing) return;
    setSyncing(true);
    try {
      const { synced, failed } = await syncSaleQueue();
      if (synced > 0) {
        setSuccess(`✅ Synced ${synced} milk sale${synced > 1 ? 's' : ''}!${failed > 0 ? ` (${failed} failed)` : ''}`);
        setTimeout(() => setSuccess(''), 4000);
        fetchData();
      }
    } catch { }
    setSyncing(false);
    refreshPending();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this sale?')) return;
    try {
      const res = await api.post(`/milk-sale/${id}/delete`);
      if (res.data?.success) { setSuccess('Deleted.'); fetchData(); setTimeout(() => setSuccess(''), 3000); }
      else setError(res.data?.message || 'Delete failed.');
    } catch (e) { setError(e.message); }
  };

  const totLiter  = rows.reduce((s,r) => s + (parseFloat(r.liter) || 0), 0);
  const totFAT    = rows.length ? rows.reduce((s,r) => s + (parseFloat(r.fat)  || 0), 0) / rows.length : 0;
  const totSNF    = rows.length ? rows.reduce((s,r) => s + (parseFloat(r.snf)  || 0), 0) / rows.length : 0;
  const totCLR    = rows.length ? rows.reduce((s,r) => s + (parseFloat(r.clr)  || 0), 0) / rows.length : 0;
  const totRate   = rows.length ? rows.reduce((s,r) => s + (parseFloat(r.rate) || 0), 0) / rows.length : 0;
  const totAmount = rows.reduce((s,r) => s + (parseFloat(r.amount) || 0), 0);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to   = Math.min(page * pageSize, total);

  const thS = (align) => ({
    fontSize:'0.8rem', fontWeight:700, color:'#374151', padding:'9px 12px',
    borderBottom:'2px solid #e5e7eb', background:'#f9fafb', whiteSpace:'nowrap',
    textAlign: align || 'left',
  });
  const tdS = (bold, align, color) => ({
    fontSize:'0.82rem', padding:'8px 12px', borderBottom:'1px solid #f3f4f6',
    whiteSpace:'nowrap', fontWeight: bold ? 700 : 400,
    textAlign: align || 'left', color: color || '#111827',
  });

  return (
    <Layout title="Milk Sale">
      {error   && <Alert severity="error"   sx={{ mb:2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb:2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {/* Offline Banner */}
      <OfflineBanner
        isOnline={isOnline}
        pendingCount={pendingCount}
        syncing={syncing}
        onSync={handleSync}
        themeColor={theme.primary}
      />

      {/* Top bar */}
      <Box sx={{ display:'flex', alignItems:'center', justifyContent:'space-between', mb:2 }}>
        <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
          <Typography sx={{ fontSize:22 }}>🥛</Typography>
          <Typography variant="h6" sx={{ fontWeight:800 }}>Milk Sale</Typography>
          {!isOnline && (
            <Chip label="Offline" size="small"
              sx={{ background:'#fff3e0', color:'#e65100', fontWeight:700, fontSize:11 }} />
          )}
        </Box>
        <Box sx={{ display:'flex', gap:1 }}>
          <Button variant="outlined" onClick={onCustomers}
            sx={{ borderRadius:2, fontWeight:700, textTransform:'none',
              borderColor:theme.primary, color:theme.primary }}>
            Customers
          </Button>
          {canAdd && <Button variant="contained" startIcon={<Add/>} onClick={onAdd}
            sx={{ borderRadius:2, fontWeight:700, textTransform:'none',
              background:`linear-gradient(135deg,${theme.primary},${theme.hover})` }}>
            + Add Milk Sale
          </Button>}
        </Box>
      </Box>

      {/* Filters */}
      <Box sx={{ display:'flex', alignItems:'center', gap:2, mb:2, flexWrap:'wrap' }}>
        <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
          <Typography sx={{ fontSize:'0.85rem', fontWeight:600 }}>Date :</Typography>
          <TextField type="date" size="small" value={date} onChange={e => setDate(e.target.value)}
            sx={{ '& .MuiOutlinedInput-root':{ borderRadius:1.5, fontSize:'0.82rem' }, width:150 }}/>
        </Box>
        <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
          <Typography sx={{ fontSize:'0.85rem', fontWeight:600 }}>Shift :</Typography>
          <FormControl size="small" sx={{ minWidth:90 }}>
            <Select value={shift} onChange={e => setShift(e.target.value)}
              sx={{ borderRadius:1.5, fontSize:'0.82rem' }}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="M">Morning</MenuItem>
              <MenuItem value="E">Evening</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
          <Typography sx={{ fontSize:'0.85rem', fontWeight:600 }}>Milk :</Typography>
          <FormControl size="small" sx={{ minWidth:90 }}>
            <Select value={milkType} onChange={e => setMilkType(e.target.value)}
              sx={{ borderRadius:1.5, fontSize:'0.82rem' }}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="C">Cow</MenuItem>
              <MenuItem value="B">Buffalo</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Button variant="contained" onClick={fetchData}
          sx={{ borderRadius:1.5, fontWeight:700, textTransform:'none',
            background:`linear-gradient(135deg,${theme.primary},${theme.hover})` }}>
          Search
        </Button>
      </Box>

      <Card sx={{ borderRadius:2, boxShadow:'0 1px 6px rgba(0,0,0,0.08)', overflow:'hidden' }}>
        {/* Toolbar */}
        <Box sx={{ display:'flex', alignItems:'center', justifyContent:'space-between',
          px:2, py:1, background:theme.light, borderBottom:`1px solid ${theme.border}` }}>
          <Typography sx={{ fontWeight:700, color:theme.primary, fontSize:'0.88rem' }}>Milk Sale</Typography>
          <TextField size="small" placeholder="Search"
            value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
            sx={{ width:180, '& .MuiOutlinedInput-root':{ borderRadius:1.5, fontSize:'0.82rem', height:32 } }}/>
        </Box>

        <Box sx={{ overflowX:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead>
              <tr>
                <th style={{ ...thS(), width:80 }}>Code ↕</th>
                <th style={thS()}>Name ↕</th>
                <th style={thS()}>Shift</th>
                <th style={thS()}>Milk</th>
                <th style={thS('right')}>Liter</th>
                <th style={thS('right')}>FAT</th>
                <th style={thS('right')}>SNF</th>
                <th style={thS('right')}>CLR</th>
                <th style={thS('right')}>Rate</th>
                <th style={thS('right')}>Amount</th>
                <th style={{ ...thS('center'), width:80 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading
                ? Array.from({length:5}).map((_,i) => (
                    <tr key={i}>{Array.from({length:11}).map((__,j) => (
                      <td key={j} style={{ padding:'8px 12px' }}><Skeleton height={14}/></td>
                    ))}</tr>
                  ))
                : rows.length === 0
                  ? <tr><td colSpan={11} style={{ textAlign:'center', padding:'40px', color:'#9ca3af', fontSize:'0.85rem' }}>
                      No Records Found.
                    </td></tr>
                  : <>
                      {rows.map((row, idx) => (
                        <tr key={row.id} style={{ background:idx%2===0?'#fff':'#fafafa' }}>
                          <td style={tdS(true)}>{row.code}</td>
                          <td style={tdS(false)}>{row.name}</td>
                          <td style={tdS(false)}>
                            <Chip label={row.shift==='M'?'M':'E'} size="small"
                              sx={{ fontSize:'0.72rem', fontWeight:700, height:20,
                                background:row.shift==='M'?'#fef3c7':'#ede9fe',
                                color:row.shift==='M'?'#92400e':'#5b21b6' }}/>
                          </td>
                          <td style={tdS(false)}>
                            <Chip label={row.milk} size="small"
                              sx={{ fontSize:'0.72rem', fontWeight:700, height:20,
                                background:row.milk==='C'?'#dcfce7':'#fce7f3',
                                color:row.milk==='C'?'#15803d':'#be185d' }}/>
                          </td>
                          <td style={tdS(false,'right')}>{parseFloat(row.liter).toFixed(2)}</td>
                          <td style={tdS(false,'right')}>{parseFloat(row.fat).toFixed(1)}</td>
                          <td style={tdS(false,'right')}>{parseFloat(row.snf).toFixed(1)}</td>
                          <td style={tdS(false,'right')}>{parseFloat(row.clr).toFixed(1)}</td>
                          <td style={tdS(false,'right')}>{parseFloat(row.rate).toFixed(1)}</td>
                          <td style={tdS(true,'right')}>{fmt2(row.amount)}</td>
                          <td style={{ ...tdS(), textAlign:'center' }}>
                            <Box sx={{ display:'flex', gap:0.5, justifyContent:'center' }}>
                              {canUpdate && <Tooltip title="Edit">
                                <IconButton size="small" sx={{ color:theme.primary, p:0.3 }}>
                                  <Edit sx={{ fontSize:16 }}/>
                                </IconButton>
                              </Tooltip>}
                              {canDelete && <Tooltip title="Delete">
                                <IconButton size="small" onClick={() => handleDelete(row.id)}
                                  sx={{ color:'#dc2626', p:0.3 }}>
                                  <Delete sx={{ fontSize:16 }}/>
                                </IconButton>
                              </Tooltip>}
                            </Box>
                          </td>
                        </tr>
                      ))}
                      {/* Totals */}
                      <tr style={{ background:'#f9fafb', borderTop:'2px solid #e5e7eb' }}>
                        <td style={tdS(true)} colSpan={2}>Total</td>
                        <td style={tdS()}>-</td>
                        <td style={tdS()}>-</td>
                        <td style={tdS(true,'right')}>{totLiter.toFixed(2)}</td>
                        <td style={tdS(true,'right')}>{totFAT.toFixed(1)}</td>
                        <td style={tdS(true,'right')}>{totSNF.toFixed(1)}</td>
                        <td style={tdS(true,'right')}>{totCLR.toFixed(1)}</td>
                        <td style={tdS(true,'right')}>{totRate.toFixed(2)}</td>
                        <td style={tdS(true,'right')}>{fmt2(totAmount)}</td>
                        <td style={tdS()}></td>
                      </tr>
                    </>
              }
            </tbody>
          </table>
        </Box>

        {/* Pagination */}
        <Box sx={{ display:'flex', alignItems:'center', justifyContent:'center',
          gap:0.5, px:2, py:1.5, borderTop:'1px solid #f0f0f0', background:'#fafafa' }}>
          <Typography sx={{ fontSize:'0.78rem', color:'#6b7280', mr:2 }}>
            Showing {from} to {to} of {total} entries
          </Typography>
          {[
            { lbl:'«', t:1,          d:page<=1 },
            { lbl:'‹', t:page-1,     d:page<=1 },
            { lbl:'›', t:page+1,     d:page>=totalPages },
            { lbl:'»', t:totalPages, d:page>=totalPages },
          ].map(({ lbl, t, d }) => (
            <IconButton key={lbl} size="small" disabled={d} onClick={() => setPage(t)}
              sx={{ width:28, height:28, border:'1px solid #e5e7eb', borderRadius:1 }}>
              <Typography sx={{ fontSize:12, fontWeight:700 }}>{lbl}</Typography>
            </IconButton>
          ))}
          <Box sx={{ mx:1, px:2, py:0.4, background:theme.primary, color:'#fff',
            borderRadius:1, fontSize:'0.78rem', fontWeight:700, textAlign:'center', minWidth:28 }}>
            {page}
          </Box>
          <select value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
            style={{ marginLeft:4, fontSize:'0.78rem', padding:'3px 6px',
              borderRadius:4, border:'1px solid #d1d5db', background:'#fff' }}>
            {[20,50,100].map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </Box>
      </Card>

      {/* Offline Queue view */}
      {pendingCount > 0 && (
        <OfflineQueueCard pendingCount={pendingCount} onRefresh={refreshPending} themeColor={theme.primary} />
      )}
    </Layout>
  );
}

// ── Offline Queue Card ──────────────────────────────────────
function OfflineQueueCard({ pendingCount, onRefresh, themeColor }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);

  const load = async () => {
    const all = await getAllSaleQueue();
    setRows(all.filter(e => e.status === 'pending'));
    setOpen(true);
  };

  return (
    <>
      <Box sx={{ mt:2, p:1.5, borderRadius:2, background:'#fff3e0', border:'1px solid #ffcc80',
        display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <Typography sx={{ fontSize:13, fontWeight:600, color:'#e65100' }}>
          💾 {pendingCount} sale{pendingCount > 1 ? 's' : ''} saved offline
        </Typography>
        <Button size="small" onClick={load}
          sx={{ fontSize:12, color: themeColor, fontWeight:700, textTransform:'none' }}>
          View
        </Button>
      </Box>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ background: themeColor, color:'#fff', fontSize:14, py:1.2,
          display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          Offline Queue ({rows.length} pending)
          <IconButton onClick={() => setOpen(false)} size="small" sx={{ color:'#fff' }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt:2 }}>
          <Box sx={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead>
                <tr style={{ background:'#f9fafb' }}>
                  {['Customer', 'Date', 'Shift', 'Milk', 'Liter', 'Rate', 'Amount', 'Saved At'].map(h => (
                    <th key={h} style={{ padding:'8px 12px', fontSize:12, fontWeight:700,
                      textAlign:'left', borderBottom:'2px solid #e5e7eb' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} style={{ background: i%2===0?'#fff':'#fafafa' }}>
                    <td style={{ padding:'8px 12px', fontSize:12 }}>{r.customerCode}</td>
                    <td style={{ padding:'8px 12px', fontSize:12 }}>{r.date}</td>
                    <td style={{ padding:'8px 12px', fontSize:12 }}>{r.shift}</td>
                    <td style={{ padding:'8px 12px', fontSize:12 }}>{r.milkType}</td>
                    <td style={{ padding:'8px 12px', fontSize:12, textAlign:'right' }}>{r.liter}</td>
                    <td style={{ padding:'8px 12px', fontSize:12, textAlign:'right' }}>{r.rate}</td>
                    <td style={{ padding:'8px 12px', fontSize:12, textAlign:'right', fontWeight:700 }}>{fmt2(r.amount)}</td>
                    <td style={{ padding:'8px 12px', fontSize:11, color:'#888' }}>
                      {new Date(r.createdAt).toLocaleString('en-IN', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ═══════════════════════════════════════════════════════════
//  VIEW 2: Add Milk Sale (with offline support)
// ═══════════════════════════════════════════════════════════
function AddMilkSaleView({ onBack, onSaved }) {
  const { theme } = useApp();
  const isOnline = useOnline();

  const [date,         setDate]         = useState(todayStr());
  const [shift,        setShift]        = useState('M');
  const [milkType,     setMilkType]     = useState('C');
  const [customerCode, setCustomerCode] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [liter,        setLiter]        = useState('');
  const [fat,          setFat]          = useState('');
  const [snf,          setSnf]          = useState('');
  const [clr,          setClr]          = useState('');
  const [rate,         setRate]         = useState('');
  const [amount,       setAmount]       = useState('0.00');
  const [paid,         setPaid]         = useState('0');
  const [balance,      setBalance]      = useState('0.00');
  const [saving,       setSaving]       = useState(false);
  const [searching,    setSearching]    = useState(false);
  const [error,        setError]        = useState('');
  const [success,      setSuccess]      = useState('');

  const codeRef  = useRef('');
  const foundRef = useRef(false);
  useEffect(() => { codeRef.current = customerCode; }, [customerCode]);

  // Auto-calc amount and balance
  useEffect(() => {
    const a = (parseFloat(liter)||0) * (parseFloat(rate)||0);
    setAmount(a.toFixed(2));
    setBalance((a - (parseFloat(paid)||0)).toFixed(2));
  }, [liter, rate, paid]);

  const lookupCustomer = async (code) => {
    if (!code) return;
    // Offline — skip lookup, just use code
    if (!isOnline) {
      setCustomerName(`Customer ${code} (offline)`);
      foundRef.current = true;
      return;
    }
    setSearching(true); setError('');
    try {
      const res = await api.get('/milk-sale/customers', { params: { search: code, pageSize: 20 } });
      const list = res.data?.data?.data || [];
      const exact = list.find(c => String(c.code) === String(code));
      if (exact) {
        setCustomerName(exact.name);
        foundRef.current = true;
      } else if (list.length === 1) {
        setCustomerName(list[0].name);
        foundRef.current = true;
      } else {
        setCustomerName(''); foundRef.current = false;
        if (list.length === 0) setError('Customer not found.');
      }
    } catch { setError('Lookup failed.'); }
    finally { setSearching(false); }
  };

  const handleReset = () => {
    setCustomerCode(''); setCustomerName('');
    setLiter(''); setFat(''); setSnf(''); setClr('');
    setRate(''); setAmount('0.00'); setPaid('0'); setBalance('0.00');
    setError(''); foundRef.current = false;
  };

  const handleSave = async () => {
    if (!customerCode) { setError('Enter customer code.'); return; }
    if (!customerName) { setError('Customer not found — press Enter to search.'); return; }
    if (!liter)        { setError('Enter liter.'); return; }
    if (!rate)         { setError('Enter rate.'); return; }

    setSaving(true);
    const payload = {
      customerCode: parseInt(customerCode) || 0,
      date, shift, milkType,
      liter:  parseFloat(liter)  || 0,
      fat:    parseFloat(fat)    || 0,
      snf:    parseFloat(snf)    || 0,
      clr:    parseFloat(clr)    || 0,
      rate:   parseFloat(rate)   || 0,
      amount: parseFloat(amount) || 0,
      paid:   parseFloat(paid)   || 0,
    };

    // ── OFFLINE: save to IndexedDB ────────────────────────
    if (!isOnline) {
      try {
        await queueSaleOffline(payload);
        setSuccess('💾 Saved offline — will sync when connected!');
        handleReset();
        setTimeout(() => { setSuccess(''); }, 3000);
      } catch (e) {
        setError('Failed to save offline: ' + e.message);
      }
      setSaving(false);
      return;
    }

    // ── ONLINE: save to server ────────────────────────────
    try {
      const res = await api.post('/milk-sale', payload);
      if (res.data?.success) {
        setSuccess('✅ Sale saved!');
        handleReset();
        setTimeout(() => { setSuccess(''); onSaved(); }, 1500);
      } else {
        setError(res.data?.message || 'Save failed.');
      }
    } catch (e) {
      // Network error → fallback to offline queue
      try {
        await queueSaleOffline(payload);
        setSuccess('📶 Network error — saved offline! Will sync when connected.');
        handleReset();
        setTimeout(() => setSuccess(''), 3000);
      } catch {
        setError('Save failed: ' + e.message);
      }
    }
    setSaving(false);
  };

  const ShiftBtn = ({ val, label, icon }) => (
    <Button variant={shift===val?'contained':'outlined'} size="small"
      startIcon={icon}
      onClick={() => setShift(val)}
      sx={{ borderRadius:2, textTransform:'none', fontWeight:600, fontSize:'0.82rem',
        ...(shift===val
          ? { background:`linear-gradient(135deg,${theme.primary},${theme.hover})` }
          : { borderColor:theme.border, color:'#374151' }) }}>
      {label}
    </Button>
  );

  const MilkBtn = ({ val, label, emoji }) => (
    <Button variant={milkType===val?'contained':'outlined'} size="small"
      onClick={() => setMilkType(val)}
      sx={{ borderRadius:2, textTransform:'none', fontWeight:600, fontSize:'0.82rem',
        ...(milkType===val
          ? { background:`linear-gradient(135deg,${theme.primary},${theme.hover})` }
          : { borderColor:theme.border, color:'#374151' }) }}>
      {emoji} {label}
    </Button>
  );

  return (
    <Layout title="Add Milk Sale">
      {error   && <Alert severity="error"   sx={{ mb:2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb:2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {/* Offline indicator */}
      {!isOnline && (
        <Alert severity="warning" icon={<CloudOff/>} sx={{ mb:2, borderRadius:2 }}>
          <strong>Offline Mode</strong> — Sales will be saved locally and synced when you're back online.
        </Alert>
      )}

      {/* Back */}
      <Box sx={{ display:'flex', alignItems:'center', gap:1, mb:3 }}>
        <IconButton size="small" onClick={onBack} sx={{ color:theme.primary }}>
          <ArrowBack/>
        </IconButton>
        <Typography sx={{ fontWeight:700, color:theme.primary, fontSize:'1rem' }}>
          Add Milk Sale
        </Typography>
        {!isOnline && (
          <Chip label="Offline" size="small"
            sx={{ background:'#fff3e0', color:'#e65100', fontWeight:700, fontSize:11 }} />
        )}
      </Box>

      {/* Date + Shift + Milk */}
      <Box sx={{ display:'flex', alignItems:'center', gap:2, mb:3, flexWrap:'wrap' }}>
        <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
          <Typography sx={{ fontSize:'0.85rem', fontWeight:600 }}>Date:</Typography>
          <TextField type="date" size="small" value={date}
            onChange={e => setDate(e.target.value)}
            sx={{ '& .MuiOutlinedInput-root':{ borderRadius:1.5, fontSize:'0.82rem' }, width:150 }}/>
        </Box>
        <Box sx={{ display:'flex', gap:1 }}>
          <ShiftBtn val="M" label="Morning" icon={<WbSunny sx={{ fontSize:15 }}/>}/>
          <ShiftBtn val="E" label="Evening" icon={<Nightlight sx={{ fontSize:15 }}/>}/>
        </Box>
        <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
          <Typography sx={{ fontSize:'0.85rem', fontWeight:600 }}>Milk:</Typography>
          <Box sx={{ display:'flex', gap:1 }}>
            <MilkBtn val="C" label="Cow"     emoji="🐄"/>
            <MilkBtn val="B" label="Buffalo" emoji="🐃"/>
          </Box>
        </Box>
      </Box>

      {/* Entry table */}
      <Card sx={{ borderRadius:2, overflow:'hidden', boxShadow:'0 1px 6px rgba(0,0,0,0.08)' }}>
        <Box sx={{ overflowX:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead>
              <tr style={{ background:'#f9fafb', borderBottom:'2px solid #e5e7eb' }}>
                {['Customer Code','Liter *','Rate *','Amount','Paid','Balance'].map(h => (
                  <th key={h} style={{ padding:'10px 12px', fontSize:'0.82rem', fontWeight:700,
                    color:'#374151', textAlign:['Liter *','Rate *','Amount','Paid','Balance'].includes(h)?'right':'left',
                    borderBottom:'1px solid #e5e7eb', whiteSpace:'nowrap' }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                {/* Customer Code */}
                <td style={{ border:'1px solid #e5e7eb', padding:'6px 8px' }}>
                  <Box sx={{ display:'flex', alignItems:'center', gap:0.5 }}>
                    <TextField size="small" placeholder="Customer code"
                      value={customerCode}
                      onChange={e => { setCustomerCode(e.target.value); setCustomerName(''); foundRef.current=false; }}
                      onKeyDown={e => { if(e.key==='Enter'){ e.preventDefault(); lookupCustomer(codeRef.current.trim()); }}}
                      onBlur={() => { if(codeRef.current.trim() && !foundRef.current) lookupCustomer(codeRef.current.trim()); }}
                      sx={{ width:120, '& .MuiOutlinedInput-root':{ fontSize:'0.82rem',
                        '& fieldset':{ border:'none' } }, '& .MuiInputBase-input':{ padding:'4px 0' } }}/>
                    {!isOnline
                      ? <CloudOff sx={{ fontSize:14, color:'#f57c00' }} />
                      : <IconButton size="small" onClick={() => lookupCustomer(codeRef.current.trim())}
                          disabled={searching} sx={{ p:0.3, color:theme.primary }}>
                          {searching ? <CircularProgress size={14}/> : <Search sx={{ fontSize:16 }}/>}
                        </IconButton>
                    }
                  </Box>
                  {customerName && (
                    <Typography sx={{ fontSize:'0.72rem', color: isOnline ? theme.primary : '#f57c00', fontWeight:600, mt:0.3 }}>
                      {isOnline ? '✅' : '💾'} {customerName}
                    </Typography>
                  )}
                </td>

                {/* Liter */}
                <td style={{ border:'1px solid #e5e7eb', padding:'6px 8px', textAlign:'right' }}>
                  <TextField size="small" type="number" value={liter} onChange={e => setLiter(e.target.value)}
                    sx={{ width:90, '& .MuiOutlinedInput-root':{ fontSize:'0.85rem', '& fieldset':{ border:'none' } },
                      '& .MuiInputBase-input':{ padding:'4px 4px', textAlign:'right' } }}/>
                </td>

                {/* Rate */}
                <td style={{ border:'1px solid #e5e7eb', padding:'6px 8px', textAlign:'right' }}>
                  <TextField size="small" type="number" value={rate} onChange={e => setRate(e.target.value)}
                    sx={{ width:90, '& .MuiOutlinedInput-root':{ fontSize:'0.85rem', '& fieldset':{ border:'none' } },
                      '& .MuiInputBase-input':{ padding:'4px 4px', textAlign:'right' } }}/>
                </td>

                {/* Amount (readonly) */}
                <td style={{ border:'1px solid #e5e7eb', padding:'6px 12px', textAlign:'right',
                  fontWeight:700, fontSize:'0.85rem', background:'#f9fafb' }}>
                  {amount}
                </td>

                {/* Paid */}
                <td style={{ border:'1px solid #e5e7eb', padding:'6px 8px', textAlign:'right' }}>
                  <TextField size="small" type="number" value={paid} onChange={e => setPaid(e.target.value)}
                    sx={{ width:90, '& .MuiOutlinedInput-root':{ fontSize:'0.85rem', '& fieldset':{ border:'none' } },
                      '& .MuiInputBase-input':{ padding:'4px 4px', textAlign:'right' } }}/>
                </td>

                {/* Balance (readonly) */}
                <td style={{ border:'1px solid #e5e7eb', padding:'6px 12px', textAlign:'right',
                  fontWeight:700, fontSize:'0.85rem', background:'#f9fafb',
                  color: parseFloat(balance)>0?'#dc2626':'#111' }}>
                  {balance}
                </td>
              </tr>
            </tbody>
          </table>
        </Box>
      </Card>

      {/* Actions */}
      <Box sx={{ display:'flex', justifyContent:'flex-end', gap:2, mt:3 }}>
        <Button variant="outlined" onClick={handleReset}
          sx={{ borderRadius:1.5, textTransform:'none', borderColor:'#d1d5db', color:'#374151', px:3 }}>
          Reset
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}
          sx={{ borderRadius:1.5, textTransform:'none', px:3,
            background: isOnline
              ? `linear-gradient(135deg,${theme.primary},${theme.hover})`
              : '#f57c00' }}>
          {saving
            ? <CircularProgress size={16} sx={{ color:'#fff' }}/>
            : isOnline ? 'Save' : '💾 Save Offline'}
        </Button>
      </Box>
    </Layout>
  );
}

// ═══════════════════════════════════════════════════════════
//  VIEW 3: Milk Sale Customers
// ═══════════════════════════════════════════════════════════
function MilkSaleCustomers({ onBack }) {
  const { theme } = useApp();
  const { user } = useAuth();
  const canAdd = canUser(user, 'MilkSale', 'add');
  const canUpdate = canUser(user, 'MilkSale', 'update');

  const [rows,     setRows]     = useState([]);
  const [total,    setTotal]    = useState(0);
  const [stats,    setStats]    = useState({ active:0, inactive:0, total:0 });
  const [loading,  setLoading]  = useState(false);
  const [search,   setSearch]   = useState('');
  const [page,     setPage]     = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [showAdd,  setShowAdd]  = useState(false);
  const [editRow,  setEditRow]  = useState(null);
  const [error,    setError]    = useState('');
  const [success,  setSuccess]  = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/milk-sale/customers', { params: { search, page, pageSize } });
      if (res.data?.success) {
        const d = res.data.data;
        setRows(d?.data || []);
        setTotal(d?.totalCount ?? 0);
        setStats({ active:d?.active??0, inactive:d?.inactive??0, total:d?.total??0 });
      }
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [search, page, pageSize]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const from = total===0?0:(page-1)*pageSize+1;
  const to   = Math.min(page*pageSize, total);

  const thS = (align) => ({
    fontSize:'0.8rem', fontWeight:700, color:'#374151', padding:'9px 14px',
    borderBottom:'2px solid #e5e7eb', background:'#fff', whiteSpace:'nowrap',
    textAlign: align || 'left',
  });
  const tdS = (bold, align, color) => ({
    fontSize:'0.82rem', padding:'8px 14px', borderBottom:'1px solid #f3f4f6',
    whiteSpace:'nowrap', fontWeight: bold ? 700 : 400,
    textAlign: align || 'left', color: color || '#111827',
  });

  return (
    <Layout title="Milk Sale Customers">
      {error   && <Alert severity="error"   sx={{ mb:2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb:2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <Box sx={{ display:'flex', alignItems:'center', justifyContent:'space-between', mb:2 }}>
        <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
          <IconButton size="small" onClick={onBack} sx={{ color:theme.primary }}>
            <ArrowBack/>
          </IconButton>
          <Typography sx={{ fontSize:18 }}>👤</Typography>
          <Typography variant="h6" sx={{ fontWeight:800 }}>Milk Sale Customers</Typography>
        </Box>
        {canAdd && <Button variant="contained" startIcon={<Add/>} onClick={() => { setEditRow(null); setShowAdd(true); }}
          sx={{ borderRadius:2, fontWeight:700, textTransform:'none',
            background:`linear-gradient(135deg,${theme.primary},${theme.hover})` }}>
          + Add Customer
        </Button>}
      </Box>

      <Card sx={{ borderRadius:2, boxShadow:'0 1px 6px rgba(0,0,0,0.08)', overflow:'hidden' }}>
        {/* Stats toolbar */}
        <Box sx={{ display:'flex', alignItems:'center', justifyContent:'space-between',
          px:2, py:1, background:theme.light, borderBottom:`1px solid ${theme.border}` }}>
          <Box sx={{ display:'flex', alignItems:'center', gap:2 }}>
            <Typography sx={{ fontWeight:700, color:theme.primary, fontSize:'0.88rem' }}>
              Milk Sale Customers
            </Typography>
            <Typography sx={{ fontSize:'0.82rem', color:'#374151' }}>
              Active : <b style={{ color:theme.primary }}>{stats.active}</b>
            </Typography>
            <Typography sx={{ fontSize:'0.82rem', color:'#374151' }}>
              Inactive : <b style={{ color:'#dc2626' }}>{stats.inactive}</b>
            </Typography>
            <Typography sx={{ fontSize:'0.82rem', color:'#374151' }}>
              Total : <b>{stats.total}</b>
            </Typography>
          </Box>
          <TextField size="small" placeholder="Search"
            value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
            sx={{ width:180, '& .MuiOutlinedInput-root':{ borderRadius:1.5, fontSize:'0.82rem', height:32 } }}/>
        </Box>

        <Box sx={{ overflowX:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead>
              <tr>
                <th style={{ ...thS(), width:80 }}>Code ↕</th>
                <th style={thS()}>Name ↕</th>
                <th style={thS()}>Milk Type</th>
                <th style={thS()}>Village</th>
                <th style={thS()}>Phone Number</th>
                <th style={thS()}>Status</th>
                <th style={{ ...thS('center'), width:60 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading
                ? Array.from({length:5}).map((_,i) => (
                    <tr key={i}>{Array.from({length:7}).map((__,j) => (
                      <td key={j} style={{ padding:'8px 14px' }}><Skeleton height={14}/></td>
                    ))}</tr>
                  ))
                : rows.length === 0
                  ? <tr><td colSpan={7} style={{ textAlign:'center', padding:'40px', color:'#9ca3af', fontSize:'0.85rem' }}>
                      No Records Found.
                    </td></tr>
                  : rows.map((row, idx) => (
                      <tr key={row.id} style={{ background:idx%2===0?'#fff':'#fafafa' }}>
                        <td style={tdS(true)}>{row.code}</td>
                        <td style={tdS(false)}>{row.name}</td>
                        <td style={tdS(false)}>
                          <Chip label={row.milkType} size="small"
                            sx={{ fontSize:'0.72rem', fontWeight:700, height:20,
                              background:'#eff6ff', color:theme.primary }}/>
                        </td>
                        <td style={tdS(false,'left','#6b7280')}>{row.village||'—'}</td>
                        <td style={tdS(false,'left','#6b7280')}>{row.phoneNumber||'—'}</td>
                        <td style={tdS(true,'left', row.status==='Active'?'#16a34a':'#dc2626')}>
                          {row.status}
                        </td>
                        <td style={{ ...tdS(), textAlign:'center' }}>
                          {canUpdate && <Tooltip title="Edit">
                            <IconButton size="small"
                              onClick={() => { setEditRow(row); setShowAdd(true); }}
                              sx={{ color:theme.primary, p:0.3 }}>
                              <Edit sx={{ fontSize:16 }}/>
                            </IconButton>
                          </Tooltip>}
                        </td>
                      </tr>
                    ))
              }
            </tbody>
          </table>
        </Box>

        {/* Pagination */}
        <Box sx={{ display:'flex', alignItems:'center', justifyContent:'center',
          gap:0.5, px:2, py:1.5, borderTop:'1px solid #f0f0f0', background:'#fafafa' }}>
          <Typography sx={{ fontSize:'0.78rem', color:'#6b7280', mr:2 }}>
            Showing {from} to {to} of {total} entries
          </Typography>
          {[
            { lbl:'«', t:1,          d:page<=1 },
            { lbl:'‹', t:page-1,     d:page<=1 },
            { lbl:'›', t:page+1,     d:page>=totalPages },
            { lbl:'»', t:totalPages, d:page>=totalPages },
          ].map(({ lbl, t, d }) => (
            <IconButton key={lbl} size="small" disabled={d} onClick={() => setPage(t)}
              sx={{ width:28, height:28, border:'1px solid #e5e7eb', borderRadius:1 }}>
              <Typography sx={{ fontSize:12, fontWeight:700 }}>{lbl}</Typography>
            </IconButton>
          ))}
          <Box sx={{ mx:1, px:2, py:0.4, background:theme.primary, color:'#fff',
            borderRadius:1, fontSize:'0.78rem', fontWeight:700, textAlign:'center', minWidth:28 }}>
            {page}
          </Box>
          <select value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
            style={{ marginLeft:4, fontSize:'0.78rem', padding:'3px 6px',
              borderRadius:4, border:'1px solid #d1d5db', background:'#fff' }}>
            {[20,50,100].map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </Box>
      </Card>

      {showAdd && (
        <AddCustomerDialog
          open={showAdd}
          editRow={editRow}
          onClose={() => { setShowAdd(false); setEditRow(null); }}
          onSaved={() => {
            fetchData(); setShowAdd(false); setEditRow(null);
            setSuccess(editRow ? 'Customer updated.' : 'Customer added.');
            setTimeout(() => setSuccess(''), 3000);
          }}
        />
      )}
    </Layout>
  );
}

// ── Add/Edit Customer Dialog ──────────────────────────────────
function AddCustomerDialog({ open, editRow, onClose, onSaved }) {
  const { theme } = useApp();
  const isEdit = !!editRow;

  const [customerName,    setCustomerName]    = useState(editRow?.name || '');
  const [phoneNumber,     setPhoneNumber]     = useState(editRow?.phoneNumber || '');
  const [milkType,        setMilkType]        = useState(editRow?.milkType || 'CB');
  const [cowRate,         setCowRate]         = useState('');
  const [buffaloRate,     setBuffaloRate]     = useState('');
  const [cowRateType,     setCowRateType]     = useState('');
  const [buffaloRateType, setBuffaloRateType] = useState('');
  const [address,         setAddress]         = useState('');
  const [village,         setVillage]         = useState(editRow?.village || '');
  const [taluka,          setTaluka]          = useState('');
  const [district,        setDistrict]        = useState('');
  const [details,         setDetails]         = useState('');
  const [status,          setStatus]          = useState(editRow?.status || 'Active');
  const [saving,          setSaving]          = useState(false);
  const [error,           setError]           = useState('');

  const handleSave = async () => {
    if (!customerName) { setError('Enter customer name.'); return; }
    setSaving(true);
    try {
      const payload = {
        customerName, phoneNumber: phoneNumber||null, milkType,
        cowRate:         cowRate         ? parseFloat(cowRate)         : null,
        buffaloRate:     buffaloRate     ? parseFloat(buffaloRate)     : null,
        cowRateType:     cowRateType     || null,
        buffaloRateType: buffaloRateType || null,
        address:  address  || null, village: village  || null,
        taluka:   taluka   || null, district:district || null,
        details:  details  || null, status,
      };
      const res = isEdit
        ? await api.post(`/milk-sale/customers/${editRow.id}/update`, payload)
        : await api.post('/milk-sale/customers', payload);
      if (res.data?.success) onSaved();
      else setError(res.data?.message || 'Save failed.');
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const lbSx = { fontSize:'0.82rem', fontWeight:600, color:'#374151', mb:0.3, display:'block' };
  const inSx = { '& .MuiOutlinedInput-root':{ borderRadius:1.5, fontSize:'0.82rem' } };
  const Section = ({ title }) => (
    <Typography sx={{ fontSize:'0.88rem', fontWeight:700, color:theme.primary, mt:1.5, mb:1 }}>
      {title}
    </Typography>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx:{ borderRadius:2.5 } }}>
      <DialogTitle sx={{ py:1.5, px:2.5, fontWeight:700, fontSize:'0.95rem',
        background:`linear-gradient(135deg,${theme.primary},${theme.hover})`, color:'#fff',
        display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        {isEdit ? 'Edit Customer' : 'Add Customer'}
        <IconButton size="small" onClick={onClose} sx={{ color:'#fff' }}>
          <Close sx={{ fontSize:18 }}/>
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px:3, pt:2, pb:1 }}>
        {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb:1.5, py:0.5 }}>{error}</Alert>}

        <Section title="Personal Details"/>
        <Box sx={{ display:'grid', gridTemplateColumns:'1fr', gap:1.5 }}>
          <Box>
            <Typography sx={lbSx}>Customer Name : <span style={{ color:'#dc2626' }}>*</span></Typography>
            <TextField size="small" fullWidth value={customerName} onChange={e => setCustomerName(e.target.value)} sx={inSx}/>
          </Box>
          <Box>
            <Typography sx={lbSx}>Phone Number :</Typography>
            <TextField size="small" fullWidth value={phoneNumber} onChange={e => setPhoneNumber(e.target.value)} sx={inSx}/>
          </Box>
        </Box>

        <Section title="Milk Details"/>
        <Box>
          <Typography sx={lbSx}>Milk Type : <span style={{ color:'#dc2626' }}>*</span></Typography>
          <FormControl size="small" sx={{ minWidth:180, ...inSx }}>
            <Select value={milkType} onChange={e => setMilkType(e.target.value)}>
              <MenuItem value="C">Cow (C)</MenuItem>
              <MenuItem value="B">Buffalo (B)</MenuItem>
              <MenuItem value="CB">Both (CB)</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Box sx={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:2, mt:1.5 }}>
          <Box>
            <Typography sx={lbSx}>Cow Milk Rate Type :</Typography>
            <FormControl size="small" fullWidth sx={inSx}>
              <Select value={cowRateType} onChange={e => setCowRateType(e.target.value)} displayEmpty>
                <MenuItem value=""><em>Select</em></MenuItem>
                <MenuItem value="FIXED">Fixed Rate</MenuItem>
                <MenuItem value="FAT_SNF">FAT/SNF Based</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box>
            <Typography sx={lbSx}>Cow Milk Rate :</Typography>
            <TextField size="small" fullWidth type="number" value={cowRate} onChange={e => setCowRate(e.target.value)} sx={inSx}/>
          </Box>
          <Box>
            <Typography sx={lbSx}>Buffalo Milk Rate Type :</Typography>
            <FormControl size="small" fullWidth sx={inSx}>
              <Select value={buffaloRateType} onChange={e => setBuffaloRateType(e.target.value)} displayEmpty>
                <MenuItem value=""><em>Select</em></MenuItem>
                <MenuItem value="FIXED">Fixed Rate</MenuItem>
                <MenuItem value="FAT_SNF">FAT/SNF Based</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box>
            <Typography sx={lbSx}>Buffalo Milk Rate :</Typography>
            <TextField size="small" fullWidth type="number" value={buffaloRate} onChange={e => setBuffaloRate(e.target.value)} sx={inSx}/>
          </Box>
        </Box>

        <Section title="Address Details"/>
        <Box sx={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:1.5 }}>
          {[
            { lbl:'Address :',  val:address,  set:setAddress  },
            { lbl:'Village:',   val:village,  set:setVillage  },
            { lbl:'Taluka:',    val:taluka,   set:setTaluka   },
            { lbl:'District :', val:district, set:setDistrict },
          ].map(({ lbl, val, set }) => (
            <Box key={lbl}>
              <Typography sx={lbSx}>{lbl}</Typography>
              <TextField size="small" fullWidth value={val} onChange={e => set(e.target.value)} sx={inSx}/>
            </Box>
          ))}
          <Box sx={{ gridColumn:'1/-1' }}>
            <Typography sx={lbSx}>Details :</Typography>
            <TextField size="small" fullWidth value={details} onChange={e => setDetails(e.target.value)} sx={inSx}/>
          </Box>
          <Box>
            <Typography sx={lbSx}>Status : <span style={{ color:'#dc2626' }}>*</span></Typography>
            <FormControl size="small" sx={{ minWidth:140, ...inSx }}>
              <Select value={status} onChange={e => setStatus(e.target.value)}>
                <MenuItem value="Active">On</MenuItem>
                <MenuItem value="Inactive">Off</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px:3, pb:2.5, gap:1 }}>
        <Button variant="outlined" onClick={onClose}
          sx={{ borderRadius:1.5, textTransform:'none', borderColor:'#d1d5db', color:'#374151', px:3 }}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}
          sx={{ borderRadius:1.5, textTransform:'none', px:3,
            background:`linear-gradient(135deg,${theme.primary},${theme.hover})` }}>
          {saving ? <CircularProgress size={16} sx={{ color:'#fff' }}/> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
